import { SearchInput } from './SearchInput';
import { FilterPopover } from './FilterPopover';
import { ActiveFilterChips } from './ActiveFilterChips';
import { GroupByMenu } from './GroupByMenu';
import { PropertiesPicker } from './PropertiesPicker';
import type { FacetDef, FilterDict, GroupByOption, PropertyChoice } from './types';

export interface ListControlBarProps {
  /** Test-id prefix passed to every child control (e.g. "work-items", "documents"). */
  testIdPrefix: string;

  /** Tag exposed on the bar's root via `data-active-tab` for view-aware styling. */
  activeTab?: string;

  searchQuery: string;
  onSearchChange: (q: string) => void;
  searchPlaceholder: string;
  searchAriaLabel: string;

  filters: FilterDict;
  onFiltersChange: (next: FilterDict) => void;
  facets: ReadonlyArray<FacetDef>;

  groupByOptions: ReadonlyArray<GroupByOption>;
  groupBy: string;
  onGroupByChange: (next: string) => void;
  groupByDisabledKeys?: ReadonlyArray<string>;

  propertiesMode: 'columns' | 'chips';
  propertyChoices: ReadonlyArray<PropertyChoice>;
  propertyLockedKeys: ReadonlyArray<string>;
  visibleProperties: ReadonlyArray<string>;
  onVisiblePropertiesChange: (next: ReadonlyArray<string>) => void;
}

/**
 * Single horizontal control bar — sits between a page's view tabs and the
 * active view pane. Renders five controls in fixed order:
 *
 *   Search · `+ Filter` · active-filter chips · spacer · `Group by ▾` · `Properties ▾`
 *
 * Responsive tiers (A · Wide, B · Mid, C · Narrow, D · Sidebar) are documented
 * in `web-mock/features/work-items/prd.md` → "Control bar — four explicit tiers"
 * and live in `src/styles/list-controls.css`.
 */
export function ListControlBar({
  testIdPrefix,
  activeTab,
  searchQuery,
  onSearchChange,
  searchPlaceholder,
  searchAriaLabel,
  filters,
  onFiltersChange,
  facets,
  groupByOptions,
  groupBy,
  onGroupByChange,
  groupByDisabledKeys,
  propertiesMode,
  propertyChoices,
  propertyLockedKeys,
  visibleProperties,
  onVisiblePropertiesChange
}: ListControlBarProps) {
  return (
    <div
      className="pn-listctl"
      data-testid={`${testIdPrefix}-controlbar`}
      data-active-tab={activeTab}
    >
      <SearchInput
        value={searchQuery}
        onChange={onSearchChange}
        placeholder={searchPlaceholder}
        ariaLabel={searchAriaLabel}
        testIdPrefix={testIdPrefix}
      />

      <FilterPopover
        filters={filters}
        onFiltersChange={onFiltersChange}
        facets={facets}
        testIdPrefix={testIdPrefix}
      />

      <ActiveFilterChips
        filters={filters}
        onFiltersChange={onFiltersChange}
        facets={facets}
        testIdPrefix={testIdPrefix}
      />

      <span className="pn-listctl-spacer" aria-hidden="true" />

      <div className="pn-listctl-right-cluster">
        <GroupByMenu
          options={groupByOptions}
          current={groupBy}
          onChange={onGroupByChange}
          disabledKeys={groupByDisabledKeys}
          testIdPrefix={testIdPrefix}
        />
        <PropertiesPicker
          mode={propertiesMode}
          choices={propertyChoices}
          lockedKeys={propertyLockedKeys}
          visible={visibleProperties}
          onVisibleChange={onVisiblePropertiesChange}
          testIdPrefix={testIdPrefix}
        />
      </div>
    </div>
  );
}
